__webpack_public_path__ = window.__webpack_public_path__;

import { createPicoX } from "@bendingspoons/picox-web";

import {
  browserName,
  isDesktop,
  isMobile,
  isTablet,
  osName,
  osVersion,
} from "react-device-detect";
import { v4 as uuidv4 } from "uuid";

const debug = window.__pico_debug__;
const disableClient = window.__pico_client_disabled__;
const disableSending = window.__pico_disable_sending__;
const environment = window.__pico_environment__ || "review";
const picoxWebIdentificationToken = window.__picox_web_identification_token__;
let picoxEnabled = Boolean(picoxWebIdentificationToken && !disableClient);

const pendingPicoXUserActionsStorageKey = "picox-pending-user-actions";

function readPendingPicoXUserActions() {
  try {
    const pending = JSON.parse(
      sessionStorage.getItem(pendingPicoXUserActionsStorageKey) || "[]",
    );
    return Array.isArray(pending)
      ? pending.filter((action) =>
          action &&
          typeof action.kind === "string" &&
          action.context != null &&
          typeof action.context === "object" &&
          !Array.isArray(action.context),
        )
      : [];
  } catch (_) {
    return [];
  }
}

function persistPendingPicoXUserActions() {
  try {
    if (pendingPicoXUserActions.length === 0) {
      sessionStorage.removeItem(pendingPicoXUserActionsStorageKey);
    } else {
      sessionStorage.setItem(
        pendingPicoXUserActionsStorageKey,
        JSON.stringify(pendingPicoXUserActions),
      );
    }
  } catch (_) {}
}

let picoX;
let picoXAdditionalContext = {};
let pendingPicoXUserActions = readPendingPicoXUserActions();
let picoXDispatchChain = Promise.resolve();
let picoXDispatchContext = null;
let cookiePersistentData = null;
let pendingPicoXAttributions = [];
const trackedPicoXAttributionUrls = new Set();

const LOCAL_STORAGE_ID_KEY = "LOCAL_STORAGE_ID_pico_lsid";
const SESSION_START_ID_KEY = "pico_ssid";
const SESSION_START_TIMESTAMP_KEY = "pico_session_start_timestamp";
const picoSessionObj = {
  user: {},
};

function getCookieValue(name) {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function getExistingPicoSession() {
  try {
    const raw = getCookieValue("pico_session");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function adblockSafeUrl(minChars = 6, maxChars = 12) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const length =
    Math.floor(Math.random() * (maxChars - minChars + 1)) + minChars;
  let token = "";
  for (let i = 0; i < length; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${window.location.origin}/${token}`;
}

function getSharedCookieDomain() {
  const hostname = location.hostname;
  if (hostname === "loomly.com") return ".loomly.com";
  if (!hostname.endsWith(".loomly.com")) return null;
  const parts = hostname.split(".");
  if (parts.length < 3) return null;
  return "." + parts.slice(1).join(".");
}

function clearHostPicoSessionCookie() {
  const attrs = [
    "path=/",
    "max-age=0",
    "SameSite=Strict",
    location.protocol === "https:" ? "Secure" : null,
  ]
    .filter(Boolean)
    .join("; ");
  document.cookie = `pico_session=; ${attrs}`;
}

function seedDeviceIdFromCookie(existingSession) {
  if (localStorage.getItem(LOCAL_STORAGE_ID_KEY)) return;
  const cookieDeviceId = existingSession?.user?.ids?.local_storage_id;
  if (cookieDeviceId) {
    localStorage.setItem(LOCAL_STORAGE_ID_KEY, cookieDeviceId);
  }
}

function persistedValue(storage, key, valueFactory) {
  try {
    const existingValue = storage.getItem(key);
    if (existingValue) return existingValue;

    const value = valueFactory();
    storage.setItem(key, value);
    return value;
  } catch (e) {
    return valueFactory();
  }
}

function localStorageId() {
  return persistedValue(localStorage, LOCAL_STORAGE_ID_KEY, uuidv4);
}

function sessionMetadata() {
  const sessionStartId = persistedValue(sessionStorage, SESSION_START_ID_KEY, uuidv4);
  const sessionStartTimestamp = persistedValue(
    sessionStorage,
    SESSION_START_TIMESTAMP_KEY,
    () => Math.floor(Date.now() / 1000).toString(),
  );

  return {
    session_start_id: sessionStartId,
    session_start_timestamp: Number(sessionStartTimestamp),
  };
}

const setPicoSessionCookie = () => {
  const sharedDomain = getSharedCookieDomain();
  if (sharedDomain) clearHostPicoSessionCookie();

  const attrs = [
    "path=/",
    "SameSite=Lax",
    sharedDomain ? `domain=${sharedDomain}` : null,
    "max-age=2592000",
    location.protocol === "https:" ? "Secure" : null,
  ]
    .filter(Boolean)
    .join("; ");

  document.cookie = `pico_session=${encodeURIComponent(JSON.stringify(picoSessionObj))}; ${attrs}`;
};

const extractUserData = () => {
  const element = document.getElementById("user-data-tag");
  return element ? JSON.parse(element.textContent) : {};
};

const extractUserId = () => extractUserData()?.id?.toString();

function extractAttributionInfo() {
  const initialParams = Object.fromEntries(
    new URLSearchParams(window.location.search),
  );

  const entries = window.__pico_query_params_allowlist
    .filter((key) => typeof initialParams[key] !== "undefined")
    .map((key) => [key, initialParams[key]]);
  return Object.fromEntries(entries);
}

function updatePicoSession(sessionContext = {}) {
  const deviceType = isMobile
    ? "mobile"
    : isTablet
      ? "tablet"
      : isDesktop
        ? "desktop"
        : "unknown";
  const userInfo = {
    device: {
      device_type: deviceType,
      language: navigator.language,
      browser: browserName,
      os: osName,
      os_version: osVersion,
    },
    locale: navigator.languages ? navigator.languages[0] : navigator.language,
  };
  const experiments = extractUserData()?.pico_experiments;

  if (experiments) {
    userInfo.experiment = experiments;
  }

  const attributionInfo = extractAttributionInfo();
  const currentPersistentUserInfo = picoSessionObj.user.info?.persistent;
  const persistentUserInfoSlice = {
    ...((currentPersistentUserInfo && typeof currentPersistentUserInfo === "object"
      ? currentPersistentUserInfo
      : cookiePersistentData) || {}),
    ...attributionInfo,
  };

  if (Object.keys(persistentUserInfoSlice).length > 0) {
    userInfo.persistent = persistentUserInfoSlice;
  }

  if (Object.keys(attributionInfo).length > 0) {
    userInfo.landing_page_info = attributionInfo;
  }

  picoSessionObj.user = {
    ids: { local_storage_id: localStorageId() },
    info: { ...userInfo, ...sessionContext },
  };
  picoSessionObj.data = sessionMetadata();
  picoXAdditionalContext = Object.fromEntries(
    Object.entries(sessionContext).filter(([, value]) => value != null),
  );
  setPicoSessionCookie();
  return picoXAdditionalContext;
}

if (!disableClient) {
  const existingSession = getExistingPicoSession();
  seedDeviceIdFromCookie(existingSession);
  cookiePersistentData = existingSession?.user?.info?.persistent || null;
  updatePicoSession(window.__pico_session_context__ || {});
}

initializePicoX();

const dispatchPicoXUserAction = (kind, info) => {
  if (disableSending) {
    debug && console.log("Sending PicoX User Action Event, but sending disabled: ", kind, info);
    return Promise.resolve();
  }

  debug && console.log("Sending PicoX User Action Event: ", kind, info);
  return picoX.track(kind, info ?? {}).catch((error) => {
    console.error("PicoX event tracking failed:", error);
    throw error;
  });
};

const enqueuePicoXUserAction = (kind, info, context = picoXAdditionalContext) => {
  // Each link resolves to the dispatch outcome (or a caught error), keeping
  // the shared chain alive; the durable queue awaits the returned promise to
  // observe failures and retry.
  const link = picoXDispatchChain.then(async () => {
    picoXDispatchContext = context;
    try {
      await dispatchPicoXUserAction(kind, info);
    } finally {
      picoXDispatchContext = null;
    }
  });
  picoXDispatchChain = link.catch((error) => error);
  return link;
};

window.trackPicoUserAction = (kind, info, context = picoXAdditionalContext) => {
  if (environment == "development") {
    require("default/pico-validator").validatePicoInfo(info);
  }
  if (!picoxEnabled) return;

  if (!picoX) {
    pendingPicoXUserActions.push({
      kind,
      info,
      context: { ...context },
    });
    persistPendingPicoXUserActions();
  } else {
    void enqueuePicoXUserAction(kind, info, context);
  }
};

document.addEventListener("turbo:load", () => {
  if (disableClient) return;
  // Snapshot THIS page's context before any await: a concurrent turbo:load
  // would otherwise repoint the global context and mis-attribute these events.
  let tagContext = null;
  const calendarContextTag = document.getElementById("pico-calendar-context");
  if (calendarContextTag) {
    try {
      tagContext = JSON.parse(calendarContextTag.textContent);
    } catch (_) {
      console.error("PicoX: malformed pico-calendar-context tag ignored");
    }
  }
  const pageContext = updatePicoSession(
    tagContext || window.__pico_session_context__ || {},
  );

  document
    .querySelectorAll(".pico-user-action-event")
    .forEach((element) => {
      let payload = {};
      try {
        payload = JSON.parse(element.textContent) || {};
      } catch (_) {
        console.error("PicoX: malformed .pico-user-action-event tag skipped");
        return;
      }
      // Events carrying user_info dispatch with THEIR fresh context, not the
      // page snapshot (the tag is the authoritative per-event context). Route
      // through the public function so external instrumentation observes it.
      let eventContext = pageContext;
      if (Object.keys(payload.user_info || {}).length > 0) {
        eventContext = updatePicoSession(payload.user_info);
      }
      window.trackPicoUserAction(payload.kind, payload.info, eventContext);
    });

  trackPicoXAttribution().catch(() => {});

  if (picoX && pendingPicoXUserActions.length > 0) {
    void dispatchPendingPicoXUserActions();
  }
});

document.addEventListener("loomly.modal.shown", (event) => {
  event.target
    .querySelectorAll(".pico-modal-user-action-event")
    .forEach((element) => {
      let payload = {};
      try {
        payload = JSON.parse(element.textContent) || {};
      } catch (_) {
        console.error("PicoX: malformed .pico-modal-user-action-event tag skipped");
        return;
      }
      if (!disableClient && Object.keys(payload.user_info || {}).length > 0) {
        updatePicoSession(payload.user_info);
      }
      window.trackPicoUserAction(payload.kind, payload.info);
    });
});

let webLockResolvers = {};

// A safe implementation of the Web Locks API that will only run if the Web Locks API is supported.
// The resolver of each lock is stored in webLockResolvers object, to release the locks on page unload.
function resolvingWebLockApiRunner(logger) {
  return async (lockId, callback) => {
    if (!navigator.locks || isMobile || isTablet) {
      logger?.debug("Web Locks API not supported");
      await callback();
      return;
    }

    logger?.debug(`Waiting for lock ${lockId}...`);
    await navigator.locks.request(lockId, async () => {
      logger?.debug(`Lock ${lockId} acquired`);
      await callback();
      return new Promise((resolve) => {
        webLockResolvers[lockId] = resolve;
      });
    });
  };
}

function onUnload() {
  for (const lockId in webLockResolvers) {
    webLockResolvers[lockId]();
  }
}

window.addEventListener("beforeunload", onUnload);
window.addEventListener("turbo:before-visit", onUnload);

async function initializePicoX() {
  if (!picoxEnabled) {
    return;
  }

  try {
    picoX = await createPicoX({
      identificationToken: picoxWebIdentificationToken,
      endpoint: adblockSafeUrl(),
      isDevelopment: environment !== "production",
      cookieDomain: getSharedCookieDomain() || location.hostname,
      identifiersSuffix: "pico_lsid",
      additionalIdentifiers: () => {
        const userId = extractUserId();
        return userId ? { user_id: userId } : {};
      },
      additionalContext: () => picoXDispatchContext || picoXAdditionalContext,
      webLockApiRunner: resolvingWebLockApiRunner(),
      logger: {
        trace: () => {},
        info: () => {},
        debug: () => {},
        warn: console.warn,
        error: console.error,
      },
    });
    await dispatchPendingPicoXAttributions();
    await trackPicoXAttribution().catch(() => {});
    await dispatchPendingPicoXUserActions();
  } catch (error) {
    if (!picoX) {
      // SDK init itself failed: no SDK object exists, tracking cannot work.
      console.error("PicoX initialization failed:", error);
      picoxEnabled = false;
    } else {
      // Initialization succeeded; a post-init send (attribution or queued
      // actions) failed. Keep tracking enabled — the durable queue retries.
      console.error("PicoX post-init dispatch failed:", error);
    }
  }
}

function picoXAttributionPayload() {
  const landingPageInfo = extractAttributionInfo();
  if (Object.keys(landingPageInfo).length === 0) return null;

  if (landingPageInfo.msclkid) {
    landingPageInfo.msclid = landingPageInfo.msclkid;
  }
  const landingPageUrl = new URL(window.location.href);
  landingPageUrl.hash = "";
  const attributionUrl = landingPageUrl.toString();
  landingPageUrl.search = "";
  landingPageInfo.t_lp = landingPageUrl.toString();

  return {
    attributionUrl,
    payload: { landing_page_info: landingPageInfo },
  };
}

async function trackPicoXAttribution() {
  const attribution = picoXAttributionPayload();
  if (!attribution || trackedPicoXAttributionUrls.has(attribution.attributionUrl)) return;

  trackedPicoXAttributionUrls.add(attribution.attributionUrl);
  if (!picoX) {
    pendingPicoXAttributions.push(attribution.payload);
    return;
  }

  await enqueuePicoXUserAction("utm_params_tracked", attribution.payload);
}

async function dispatchPendingPicoXAttributions() {
  const pending = pendingPicoXAttributions;
  pendingPicoXAttributions = [];

  for (const payload of pending) {
    try {
      await enqueuePicoXUserAction("utm_params_tracked", payload);
    } catch (error) {
      console.error("PicoX attribution dispatch failed:", error);
    }
  }
}

let dispatchInFlight = false;

async function dispatchPendingPicoXUserActions() {
  if (dispatchInFlight) return;
  dispatchInFlight = true;
  try {
    await dispatchPendingPicoXUserActionsInner();
  } finally {
    dispatchInFlight = false;
  }
}

async function dispatchPendingPicoXUserActionsInner() {
  while (pendingPicoXUserActions.length > 0) {
    const { kind, info, context } = pendingPicoXUserActions[0];
    try {
      await enqueuePicoXUserAction(kind, info, context);
    } catch (error) {
      const head = pendingPicoXUserActions[0];
      head.dispatchFailures = (head.dispatchFailures || 0) + 1;
      persistPendingPicoXUserActions();
      if (head.dispatchFailures >= 3) {
        console.error("PicoX durable queue: dropping action after 3 failed dispatches", kind, error);
        pendingPicoXUserActions.shift();
        persistPendingPicoXUserActions();
        continue;
      }
      break;
    }
    pendingPicoXUserActions.shift();
    persistPendingPicoXUserActions();
  }
}
